import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Fingerprint, Eye, EyeOff, Globe, Monitor, Cpu, Languages,
  Clock, Shield, Save, RefreshCw, ChevronDown, ChevronUp
} from "lucide-react";
import { toast } from "sonner";

const API = '/api';

const RESOLUTIONS = [
  "1920x1080", "1366x768", "1536x864", "1440x900",
  "1280x720", "1600x900", "2560x1440", "1280x800"
];

const TIMEZONES = [
  "America/Mexico_City", "America/Bogota", "America/Lima",
  "America/Buenos_Aires", "America/Santiago", "America/New_York",
  "America/Los_Angeles", "Europe/Madrid", "America/Sao_Paulo"
];

const LANGUAGES = [
  "es-MX", "es-ES", "es-AR", "es-CO", "en-US", "pt-BR", "fr-FR"
];

const ToggleSwitch = ({ checked, onChange, testId }) => (
  <button
    data-testid={testId}
    onClick={() => onChange(!checked)}
    className={`w-11 h-6 rounded-full transition-colors duration-200 relative flex-shrink-0 ${
      checked ? 'bg-blue-600' : 'bg-zinc-700'
    }`}
  >
    <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform duration-200 ${
      checked ? 'translate-x-5' : 'translate-x-0.5'
    }`} />
  </button>
);

export default function AntiDetect() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProfile, setExpandedProfile] = useState(null);
  const [configs, setConfigs] = useState({});
  const [saving, setSaving] = useState({});

  const fetchProfiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profiles`);
      setProfiles(res.data);
      for (const p of res.data) {
        try {
          const fp = await axios.get(`${API}/profiles/${p.id}/fingerprint`);
          setConfigs(prev => ({ ...prev, [p.id]: fp.data }));
        } catch (e) {
          setConfigs(prev => ({ ...prev, [p.id]: getDefaultConfig() }));
        }
      }
    } catch (e) {
      // error silenced
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const getDefaultConfig = () => ({
    canvas_noise: true,
    webgl_noise: true,
    webrtc_block: true,
    user_agent_rotation: true,
    timezone_spoof: null,
    language_spoof: null,
    screen_resolution: null,
    plugins_spoof: true,
    hardware_concurrency: null,
    device_memory: null
  });

  const updateConfig = (profileId, key, value) => {
    setConfigs(prev => ({
      ...prev,
      [profileId]: { ...prev[profileId], [key]: value }
    }));
  };

  const saveConfig = async (profileId) => {
    setSaving(prev => ({ ...prev, [profileId]: true }));
    try {
      await axios.put(`${API}/profiles/${profileId}/fingerprint`, configs[profileId]);
      toast.success("Configuracion anti-deteccion guardada");
    } catch (e) {
      toast.error("Error al guardar configuracion");
    } finally {
      setSaving(prev => ({ ...prev, [profileId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="antidetect-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white uppercase">
          Anti-Deteccion
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Configura el fingerprint de cada perfil para evitar que Google detecte multiples sesiones
        </p>
      </div>

      <div className="bg-zinc-900/50 border border-amber-500/20 rounded-lg p-4 flex items-start gap-3">
        <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-amber-300 font-medium">Proteccion por Perfil</p>
          <p className="text-xs text-zinc-400 mt-1">
            Cada perfil de extension puede tener un fingerprint unico. Esto evita que Google detecte
            que multiples numeros estan enviando desde el mismo navegador. La extension inyectara estas
            configuraciones automaticamente al conectarse.
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {profiles.map((profile, i) => {
          const config = configs[profile.id] || getDefaultConfig();
          const isExpanded = expandedProfile === profile.id;

          return (
            <div
              key={profile.id}
              data-testid={`antidetect-profile-${i}`}
              className={`bg-zinc-900 border rounded-lg overflow-hidden transition-colors duration-200 ${
                isExpanded ? 'border-blue-500/30' : 'border-white/5'
              }`}
            >
              <button
                data-testid={`expand-profile-${i}`}
                onClick={() => setExpandedProfile(isExpanded ? null : profile.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors duration-200"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${
                    profile.status === 'connected' ? 'bg-emerald-500 glow-dot' : 'bg-zinc-600'
                  }`} />
                  <div className="text-left">
                    <p className="text-sm font-semibold text-white">{profile.name}</p>
                    <p className="text-[10px] font-mono text-zinc-600">{profile.phone_number || "Sin numero"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex gap-1">
                    {config.canvas_noise && <span className="w-1.5 h-1.5 rounded-full bg-blue-400" title="Canvas" />}
                    {config.webgl_noise && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" title="WebGL" />}
                    {config.webrtc_block && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" title="WebRTC" />}
                    {config.user_agent_rotation && <span className="w-1.5 h-1.5 rounded-full bg-purple-400" title="UA" />}
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-white/5 p-5 space-y-5 animate-fade-in">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* Canvas Noise */}
                    <div className="flex items-center justify-between bg-zinc-950/50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-600/10 flex items-center justify-center">
                          <Eye className="w-4 h-4 text-blue-400" />
                        </div>
                        <div>
                          <p className="text-sm text-white font-medium">Ruido Canvas</p>
                          <p className="text-[10px] text-zinc-500">Inyecta variaciones en Canvas 2D</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        checked={config.canvas_noise}
                        onChange={(v) => updateConfig(profile.id, 'canvas_noise', v)}
                        testId={`canvas-noise-${i}`}
                      />
                    </div>

                    {/* WebGL Noise */}
                    <div className="flex items-center justify-between bg-zinc-950/50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-600/10 flex items-center justify-center">
                          <Monitor className="w-4 h-4 text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-sm text-white font-medium">Ruido WebGL</p>
                          <p className="text-[10px] text-zinc-500">Modifica vendor/renderer WebGL</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        checked={config.webgl_noise}
                        onChange={(v) => updateConfig(profile.id, 'webgl_noise', v)}
                        testId={`webgl-noise-${i}`}
                      />
                    </div>

                    {/* WebRTC Block */}
                    <div className="flex items-center justify-between bg-zinc-950/50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-600/10 flex items-center justify-center">
                          <EyeOff className="w-4 h-4 text-amber-400" />
                        </div>
                        <div>
                          <p className="text-sm text-white font-medium">Bloquear WebRTC</p>
                          <p className="text-[10px] text-zinc-500">Previene fuga de IP real</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        checked={config.webrtc_block}
                        onChange={(v) => updateConfig(profile.id, 'webrtc_block', v)}
                        testId={`webrtc-block-${i}`}
                      />
                    </div>

                    {/* User Agent Rotation */}
                    <div className="flex items-center justify-between bg-zinc-950/50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-purple-600/10 flex items-center justify-center">
                          <Globe className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-sm text-white font-medium">Rotar User-Agent</p>
                          <p className="text-[10px] text-zinc-500">Cambia UA en cada sesion</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        checked={config.user_agent_rotation}
                        onChange={(v) => updateConfig(profile.id, 'user_agent_rotation', v)}
                        testId={`ua-rotation-${i}`}
                      />
                    </div>

                    {/* Plugins Spoof */}
                    <div className="flex items-center justify-between bg-zinc-950/50 rounded-lg p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-cyan-600/10 flex items-center justify-center">
                          <Cpu className="w-4 h-4 text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-sm text-white font-medium">Falsear Plugins</p>
                          <p className="text-[10px] text-zinc-500">Modifica lista de plugins</p>
                        </div>
                      </div>
                      <ToggleSwitch
                        checked={config.plugins_spoof}
                        onChange={(v) => updateConfig(profile.id, 'plugins_spoof', v)}
                        testId={`plugins-spoof-${i}`}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
                        Zona Horaria
                      </label>
                      <select
                        data-testid={`timezone-${i}`}
                        value={config.timezone_spoof || ""}
                        onChange={(e) => updateConfig(profile.id, 'timezone_spoof', e.target.value || null)}
                        className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none transition-colors duration-200"
                      >
                        <option value="">Auto (real)</option>
                        {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
                        Idioma Navegador
                      </label>
                      <select
                        data-testid={`language-${i}`}
                        value={config.language_spoof || ""}
                        onChange={(e) => updateConfig(profile.id, 'language_spoof', e.target.value || null)}
                        className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none transition-colors duration-200"
                      >
                        <option value="">Auto (real)</option>
                        {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
                        Resolucion Pantalla
                      </label>
                      <select
                        data-testid={`resolution-${i}`}
                        value={config.screen_resolution || ""}
                        onChange={(e) => updateConfig(profile.id, 'screen_resolution', e.target.value || null)}
                        className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none transition-colors duration-200"
                      >
                        <option value="">Auto (real)</option>
                        {RESOLUTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
                        Nucleos CPU (hardwareConcurrency)
                      </label>
                      <select
                        data-testid={`cpu-cores-${i}`}
                        value={config.hardware_concurrency || ""}
                        onChange={(e) => updateConfig(profile.id, 'hardware_concurrency', e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none transition-colors duration-200"
                      >
                        <option value="">Auto (real)</option>
                        {[2, 4, 6, 8, 12, 16].map(n => <option key={n} value={n}>{n} nucleos</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">
                        Memoria Dispositivo (GB)
                      </label>
                      <select
                        data-testid={`device-memory-${i}`}
                        value={config.device_memory || ""}
                        onChange={(e) => updateConfig(profile.id, 'device_memory', e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-blue-500 rounded-md px-3 py-2 text-sm text-white outline-none transition-colors duration-200"
                      >
                        <option value="">Auto (real)</option>
                        {[2, 4, 8, 16, 32].map(n => <option key={n} value={n}>{n} GB</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button
                      data-testid={`save-fingerprint-${i}`}
                      onClick={() => saveConfig(profile.id)}
                      disabled={saving[profile.id]}
                      className="bg-blue-600 hover:bg-blue-700 text-white shadow-[0_0_15px_rgba(37,99,235,0.3)] rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors duration-200 active:scale-95 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {saving[profile.id] ? "Guardando..." : "Guardar Fingerprint"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {profiles.length === 0 && (
          <div className="text-center py-12">
            <Fingerprint className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No hay perfiles creados</p>
            <p className="text-zinc-600 text-xs mt-1">Crea un perfil primero para configurar anti-deteccion</p>
          </div>
        )}
      </div>
    </div>
  );
}
